import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, User, Plus, Car, Save, Edit3 } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { fmtDate } from "../lib/format.js";
import { useAuth } from "../lib/auth.jsx";
import WarrantyStatusBadge from "../components/WarrantyStatusBadge.jsx";
import StatusBadge from "../components/StatusBadge.jsx";
import NewVehicleRegistrationDialog from "../components/NewVehicleRegistrationDialog.jsx";

const TABS = [
  { key: "profile", label: "Perfil" },
  { key: "vehicles", label: "Viaturas" },
  { key: "soms", label: "Histórico de SOMs" },
];

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [soms, setSoms] = useState([]);
  const [tab, setTab] = useState("profile");
  const [editing, setEditing] = useState(false);
  const [showNewVr, setShowNewVr] = useState(false);
  const [enums, setEnums] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");

  const load = async () => {
    const [{ data: c }, { data: s }, { data: e }] = await Promise.all([
      api.get(`/customers/${id}`),
      api.get(`/customers/${id}/service-cases`),
      api.get(`/enums`),
    ]);
    setCustomer(c);
    setSoms(s);
    setEnums(e);
    setForm({
      name: c.name || "",
      id_number: c.id_number || "",
      phone: c.phone || "",
      phone_secondary: c.phone_secondary || "",
      email: c.email || "",
      address: c.address || "",
      notes: c.notes || "",
    });
  };

  useEffect(() => { load(); }, [id]);

  if (!customer) return <div className="p-8 text-sm text-[#6C757D]">A carregar…</div>;

  const canEdit = ["admin", "national_aftersales_manager", "department_manager"].includes(user?.role)
    || (user?.effective_modules || []).includes("vehicle_registrations");

  const save = async () => {
    setError("");
    try {
      const { data } = await api.patch(`/customers/${id}`, form);
      setCustomer(data);
      setEditing(false);
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    }
  };

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 shadow-sm" data-testid="customer-sticky-header">
        <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
          <Link to="/customers" className="flex items-center gap-1 hover:text-[#1F3864]">
            <ChevronLeft className="w-3 h-3" /> Clientes
          </Link>
          <span>/</span>
          <span className="font-mono">{customer.customer_number}</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <User className="w-5 h-5 text-[#1F3864]" />
              <h1 className="font-heading text-xl font-bold text-[#1A1A2E]" data-testid="customer-name">{customer.name}</h1>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6C757D]">
              <span className="font-mono">{customer.phone}</span>
              {customer.email && <span>{customer.email}</span>}
              {customer.id_number && <span>NUI: <span className="font-mono">{customer.id_number}</span></span>}
              <span>{customer.vehicle_count} viatura{customer.vehicle_count === 1 ? "" : "s"}</span>
            </div>
          </div>
          {canEdit && !editing && (
            <button onClick={() => setEditing(true)} className="text-sm border border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864] hover:text-white rounded-sm px-3 py-1.5 flex items-center gap-2" data-testid="customer-edit-btn">
              <Edit3 className="w-3.5 h-3.5" /> Editar
            </button>
          )}
        </div>

        <div className="mt-3 flex gap-1 border-b -mb-3">
          {TABS.map((t) => {
            const suffix = t.key === "vehicles" ? ` (${customer.vehicle_count})`
              : t.key === "soms" ? ` (${soms.length})` : "";
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`customer-tab-${t.key}`}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? "border-[#1F3864] text-[#1F3864]" : "border-transparent text-[#6C757D] hover:text-[#1F3864]"}`}
              >
                {t.label}{suffix}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {tab === "profile" && (
          <div className="bg-white border border-gray-200 rounded-md p-5">
            {error && <div className="mb-3 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">{error}</div>}
            {editing ? (
              <div className="grid grid-cols-2 gap-3">
                <FieldEdit label="Nome *" value={form.name} onChange={(v) => change("name", v)} />
                <FieldEdit label="NUI / Passaporte" value={form.id_number} onChange={(v) => change("id_number", v)} />
                <FieldEdit label="Telefone *" value={form.phone} onChange={(v) => change("phone", v)} />
                <FieldEdit label="Telefone secundário" value={form.phone_secondary} onChange={(v) => change("phone_secondary", v)} />
                <FieldEdit label="Email" type="email" value={form.email} onChange={(v) => change("email", v)} />
                <FieldEdit label="Morada" value={form.address} onChange={(v) => change("address", v)} />
                <div className="col-span-2">
                  <FieldEdit label="Notas" textarea value={form.notes} onChange={(v) => change("notes", v)} />
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button onClick={() => { setEditing(false); setError(""); }} className="px-4 py-2 text-sm border border-gray-300 rounded-sm">Cancelar</button>
                  <button onClick={save} className="px-4 py-2 text-sm bg-[#1F3864] text-white rounded-sm flex items-center gap-2" data-testid="customer-save-btn">
                    <Save className="w-3.5 h-3.5" /> Guardar
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <Row label="Nome">{customer.name}</Row>
                <Row label="Nº Cliente"><span className="font-mono">{customer.customer_number}</span></Row>
                <Row label="Telefone">{customer.phone}</Row>
                <Row label="Telefone secundário">{customer.phone_secondary || "—"}</Row>
                <Row label="Email">{customer.email || "—"}</Row>
                <Row label="NUI / Passaporte">{customer.id_number || "—"}</Row>
                <Row label="Morada">{customer.address || "—"}</Row>
                <Row label="Data de registo">{fmtDate(customer.created_at)}</Row>
                <div className="col-span-2">
                  <Row label="Notas">{customer.notes || "—"}</Row>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "vehicles" && (
          <>
            <div className="flex justify-end mb-3">
              {canEdit && (
                <button onClick={() => setShowNewVr(true)} className="bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-3 py-2 text-sm font-medium flex items-center gap-2" data-testid="customer-new-vr-btn">
                  <Plus className="w-4 h-4" /> Registar nova viatura
                </button>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
              {(customer.vehicles || []).length === 0 ? (
                <div className="p-8 text-center text-[#6C757D] text-sm" data-testid="customer-vehicles-empty">Este cliente não tem viaturas registadas.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-widest text-[#6C757D]">
                    <tr><Th>VR</Th><Th>Matrícula</Th><Th>Marca / Modelo</Th><Th>Ano</Th><Th>Cor</Th><Th>Data venda</Th><Th>Garantia</Th><Th>Dealer</Th></tr>
                  </thead>
                  <tbody>
                    {customer.vehicles.map((v) => (
                      <tr key={v.id} onClick={() => navigate(`/vehicle-registrations/${v.id}`)} data-testid={`customer-vehicle-row-${v.id}`} className="border-t border-gray-100 hover:bg-[#F4F6F8] cursor-pointer">
                        <Td mono>{v.vr_number}</Td>
                        <Td mono className="text-[#1F3864] font-semibold">{v.registration_plate}</Td>
                        <Td>{v.brand} {v.model}</Td>
                        <Td>{v.year}</Td>
                        <Td>{v.colour}</Td>
                        <Td>{fmtDate(v.sale_date)}</Td>
                        <Td><WarrantyStatusBadge status={v.warranty_status} daysToExpiry={v.warranty_days_to_expiry} /></Td>
                        <Td>{v.branch}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === "soms" && (
          <div className="bg-white border border-gray-200 rounded-md">
            {soms.length === 0 ? (
              <div className="p-8 text-center text-[#6C757D] text-sm" data-testid="customer-soms-empty">Este cliente ainda não tem obras de manutenção registadas.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-widest text-[#6C757D]">
                  <tr><Th>SOM</Th><Th>Data</Th><Th>Matrícula</Th><Th>Marca / Modelo</Th><Th>Estado</Th><Th>Dealer</Th></tr>
                </thead>
                <tbody>
                  {soms.map((s) => (
                    <tr key={s.id} onClick={() => navigate(`/service-cases/${s.id}`)} data-testid={`customer-som-row-${s.id}`} className="border-t border-gray-100 hover:bg-[#F4F6F8] cursor-pointer">
                      <Td mono className="text-[#1F3864] font-semibold">{s.number}</Td>
                      <Td>{fmtDate(s.opened_at)}</Td>
                      <Td mono>{s.vehicle_registration}</Td>
                      <Td>{s.brand} {s.model}</Td>
                      <Td><StatusBadge status={s.current_status} /></Td>
                      <Td>{s.branch}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {showNewVr && enums && (
        <NewVehicleRegistrationDialog
          enums={enums}
          presetCustomerId={customer.id}
          presetCustomerName={`${customer.customer_number} · ${customer.name} · ${customer.phone}`}
          onClose={() => setShowNewVr(false)}
          onCreated={(vr) => { setShowNewVr(false); navigate(`/vehicle-registrations/${vr.id}`); }}
        />
      )}
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-3 py-2 whitespace-nowrap">{children}</th>; }
function Td({ children, mono, className = "" }) { return <td className={`px-3 py-2 whitespace-nowrap ${mono ? "font-mono" : ""} ${className}`}>{children}</td>; }
function Row({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className="text-sm text-[#1A1A2E]">{children}</div>
    </div>
  );
}
function FieldEdit({ label, value, onChange, type = "text", textarea }) {
  const cls = "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls + " h-20"} />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cls} />
      )}
    </label>
  );
}
